import { Button, Group, TextInput, Text, Select, Box, Input } from '@mantine/core';

import { useProphetRuleForm } from '../../../hooks/useProphetRuleForm';
import { prophetTypes } from '../../../types';
import { ChunkPlatform } from '../../../utils/chunk-limits';

import { Intervals } from './Intervals';
import { Messages } from './Messages';
import { Requirements } from './Requirements';

import { FunnelsSelect } from '@/components/Selects/Funnels';

const successActionOptions = [
  { value: 'sendToManager', label: 'Отправить менеджеру' },
  { value: 'reply', label: 'Ответить' },
];

const failActionOptions = [
  { value: 'sendToManager', label: 'Отправить менеджеру' },
  { value: 'remove', label: 'Удалить' },
];

const noReponseActionOptions = [
  { value: 'remove', label: 'Удалить' },
  { value: 'reply', label: 'Ответить' },
];

type Props = {
  onSubmit: (data: Omit<Parameters<prophetTypes.AddProphetRule>[0], 'page'>) => void;
  appPageId: string;
  isLoading: boolean;
  initialValues?: Omit<prophetTypes.Rule, 'page' | '_id'>;
  isKeywordInputDisabled?: boolean;
  // Drives the per-platform chunk-size gate in the text editors and the
  // form-level validator. instagram = byte gate (~900), telegram = char
  // gate (~990). See utils/chunk-limits.ts for the rationale.
  platform: ChunkPlatform;
};

export const RuleForm = ({
  onSubmit,
  appPageId,
  isLoading,
  initialValues,
  isKeywordInputDisabled,
  platform,
}: Props) => {
  const form = useProphetRuleForm({ initialValues, platform });

  const isFormError = Object.keys(form.errors).length > 0;

  return (
    <form onSubmit={form.onSubmit(onSubmit)}>
      <TextInput
        disabled={isKeywordInputDisabled}
        label="Кодовое слово"
        placeholder="Кодовое слово"
        value={form.values.keyword}
        onChange={(event) => form.setFieldValue('keyword', event.currentTarget.value)}
        my="sm"
        error={form.errors.keyword}
      />
      <Select
        label="Действие в случае успеха"
        withAsterisk
        data={successActionOptions}
        value={form.values.actions.success.type}
        onChange={(value) => {
          form.setFieldValue(
            'actions.success.type',
            value as prophetTypes.Rule['actions']['success']['type']
          );
          if (value === 'sendToManager') {
            form.setFieldValue('actions.success', {
              type: 'sendToManager',
              funnelId: '',
            });
          }
          if (value === 'reply') {
            form.setFieldValue('actions.success', {
              type: 'reply',
            });
          }
          form.setFieldValue('texts', {
            greetings: [],
            discounts: [],
            preparing: [],
            spreads: [],
            spreadKicks: [],
          });
        }}
        error={form.errors.action}
        mb={
          !(form.values.actions.success.type === 'sendToManager' && form.values.keyword)
            ? 'sm'
            : undefined
        }
      />
      {form.values.actions.success.type === 'sendToManager' && form.values.keyword && (
        <Box mb="sm">
          <Input.Label>Воронка при успехе</Input.Label>
          <FunnelsSelect
            required={false}
            pageId={appPageId}
            placeholder="Выберите воронку"
            value={
              form.values.actions.success.funnelId ? [form.values.actions.success.funnelId] : []
            }
            onSelect={(value) => form.setFieldValue('actions.success.funnelId', value[0])}
            error={form.errors['actions?.success?.funnelId']}
          />
        </Box>
      )}
      <Select
        label="Действие в случае неудачи"
        withAsterisk
        data={failActionOptions}
        value={form.values.actions.fail}
        onChange={(value) =>
          value && form.setFieldValue('actions.fail', value as prophetTypes.Rule['actions']['fail'])
        }
        error={form.errors.action}
        mb="sm"
      />
      <Select
        label="Действие в случае отсутствия ответа на требования"
        withAsterisk
        data={noReponseActionOptions}
        value={form.values.actions.noResponse}
        onChange={(value) =>
          value &&
          form.setFieldValue(
            'actions.noResponse',
            value as prophetTypes.Rule['actions']['noResponse']
          )
        }
        error={form.errors.noResponse}
        mb="sm"
      />
      <Intervals form={form} />
      <Requirements form={form} />
      <Messages form={form} platform={platform} />
      <Box h={25}>
        {isFormError && (
          <Text c="red" size="sm" ta="end">
            {Object.values(form.errors)[0] || 'Ошибка'}
          </Text>
        )}
      </Box>
      <Group justify="flex-end">
        <Button loading={isLoading} type="submit">
          {initialValues ? 'Сохранить' : 'Создать'}
        </Button>
      </Group>
    </form>
  );
};
